/**
 * Access Control Management Component
 *
 * Main admin interface for managing access control:
 * - Whitelisted domains
 * - Whitelisted users
 * - Access requests (pending/approved/denied)
 */

import React, { useState } from 'react';
import { DomainsTab } from './DomainsTab';
import { UsersTab } from './UsersTab';
import { RequestsTab } from './RequestsTab';

type TabType = 'requests' | 'domains' | 'users';

export const AccessControlManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('requests');

  const tabs: { id: TabType; label: string; description: string }[] = [
    {
      id: 'requests',
      label: 'Access Requests',
      description: 'Review and manage user access requests',
    },
    {
      id: 'domains',
      label: 'Whitelisted Domains',
      description: 'Manage trusted domains with auto-approval',
    },
    {
      id: 'users',
      label: 'Whitelisted Users',
      description: 'Manage individual user access',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Access Control Management
        </h1>
        <p className="text-gray-600">
          Manage user access, whitelisted domains, and access requests
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow sm:rounded-lg p-6">
        {activeTab === 'requests' && <RequestsTab />}
        {activeTab === 'domains' && <DomainsTab />}
        {activeTab === 'users' && <UsersTab />}
      </div>
    </div>
  );
};
